// ============================================================
// HV-TaskFlow V1.1.0 — Nút bật thông báo + hướng dẫn cài đặt (PWA).
//
// Hiển thị đúng theo từng trạng thái, KHÔNG mời gọi một thứ chắc chắn thất bại:
//   - iOS chưa "Thêm vào Màn hình chính" → hiện HƯỚNG DẪN CÀI (không hiện nút bật,
//     vì bấm cũng vô ích: iOS không cho phép).
//   - Đã cài / Android / desktop        → hiện nút "Bật thông báo".
//   - Người dùng đã từ chối quyền       → hướng dẫn mở lại trong Cài đặt.
// ============================================================
import { useEffect, useState } from 'react';
import { Alert, Button, Space, Typography, App } from 'antd';
import { Bell, BellOff, Share, PlusSquare } from 'lucide-react';
import {
  enablePush, disablePush, isPushEnabled, getPushSupport, sendTestPush,
  type PushSupport,
} from '@/services/pushService';

const { Text } = Typography;

export function PushOptIn() {
  const { message } = App.useApp();
  const [support, setSupport] = useState<PushSupport>('unsupported');
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setSupport(getPushSupport());
    isPushEnabled().then(setEnabled).catch(() => setEnabled(false));
  }, []);

  const handleEnable = async () => {
    setLoading(true);
    try {
      const ok = await enablePush();
      if (ok) {
        setEnabled(true);
        message.success('Đã bật thông báo trên thiết bị này');
      } else {
        message.warning('Chưa bật được thông báo. Vui lòng kiểm tra quyền thông báo của trình duyệt.');
      }
    } catch {
      message.error('Không bật được thông báo');
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async () => {
    setLoading(true);
    try {
      await disablePush();
      setEnabled(false);
      message.success('Đã tắt thông báo trên thiết bị này');
    } catch {
      message.error('Không tắt được thông báo');
    } finally {
      setLoading(false);
    }
  };

  // --- iOS chưa cài ra màn hình chính: bấm nút cũng vô ích, phải hướng dẫn cài ---
  if (support === 'needs-install') {
    return (
      <Alert
        type="info"
        showIcon
        message="Cài HV-TaskFlow vào máy để nhận thông báo"
        description={
          <div style={{ fontSize: 13 }}>
            <div style={{ marginBottom: 6 }}>
              Trên iPhone/iPad, thông báo chỉ hoạt động sau khi thêm ứng dụng vào Màn hình chính:
            </div>
            <Space direction="vertical" size={2}>
              <Text>
                1. Bấm nút <Share size={13} style={{ verticalAlign: -2 }} /> <b>Chia sẻ</b> ở thanh dưới Safari
              </Text>
              <Text>
                2. Chọn <PlusSquare size={13} style={{ verticalAlign: -2 }} /> <b>Thêm vào MH chính</b>
              </Text>
              <Text>3. Mở lại ứng dụng từ biểu tượng trên màn hình chính</Text>
            </Space>
          </div>
        }
      />
    );
  }

  if (support === 'denied') {
    return (
      <Alert
        type="warning"
        showIcon
        message="Thông báo đang bị chặn"
        description="Bạn đã từ chối quyền thông báo. Vui lòng mở lại trong Cài đặt trình duyệt để nhận thông báo công việc."
      />
    );
  }

  if (support === 'unsupported') {
    return (
      <Alert
        type="warning"
        showIcon
        message="Trình duyệt không hỗ trợ thông báo"
        description="Vui lòng dùng Chrome (Android/máy tính) hoặc Safari (iOS 16.4 trở lên)."
      />
    );
  }

  // --- Đăng ký được ---
  return (
    <Space direction="vertical" style={{ width: '100%' }} size={8}>
      {enabled ? (
        <>
          <Alert
            type="success"
            showIcon
            message="Đã bật thông báo trên thiết bị này"
            description="Bạn sẽ nhận được thông báo khi được giao việc, công việc sắp đến hạn, hoặc có tài liệu cần ký — kể cả khi không mở ứng dụng."
          />
          <Space>
            <Button icon={<BellOff size={14} />} loading={loading} onClick={handleDisable}>
              Tắt thông báo
            </Button>
            <Button
              type="link"
              onClick={async () => {
                await sendTestPush();
                message.info('Đã gửi thông báo thử — kiểm tra máy của bạn');
              }}
            >
              Gửi thử
            </Button>
          </Space>
        </>
      ) : (
        <>
          <Alert
            type="info"
            showIcon
            message="Bật thông báo để không bỏ lỡ công việc"
            description="Nhận thông báo ngay khi được giao việc hoặc có tài liệu cần ký, kể cả khi đóng ứng dụng."
          />
          <Button
            type="primary"
            icon={<Bell size={14} />}
            loading={loading}
            onClick={handleEnable}
          >
            Bật thông báo
          </Button>
        </>
      )}
    </Space>
  );
}
